import { useEffect, useMemo, useState } from "react";
import type { CSSProperties, FormEvent, ReactNode } from "react";
import {
  AlertTriangle,
  ArrowDownToLine,
  Archive,
  BadgeCheck,
  Banknote,
  Bell,
  Building2,
  CheckCircle2,
  ChevronRight,
  ClipboardCheck,
  Database,
  FileCheck2,
  FilePlus2,
  FileText,
  Gauge,
  Landmark,
  LayoutDashboard,
  LockKeyhole,
  LogOut,
  MessageSquareText,
  PencilLine,
  PieChart,
  Plus,
  Route,
  Save,
  Search,
  Send,
  Settings2,
  ShieldCheck,
  SlidersHorizontal,
  Trash2,
  UserCog,
  UserPlus,
  Workflow,
  XCircle
} from "lucide-react";
import {
  auditEvents as seededAuditEvents,
  bankFormRequests as seededBankFormRequests,
  bankFormTemplates as seededBankFormTemplates,
  bankProducts as seededBankProducts,
  clientTasks as seededClientTasks,
  demoApplications as seededApplications,
  productionChecklist,
  ruleDefinitions as seededRuleDefinitions
} from "./data/demoData";
import {
  formatCurrency,
  formatPercent,
  riskLabel,
  routeLabel,
  scoreApplication
} from "./domain/scoring";
import type {
  Application,
  ApplicationStatus,
  AuditEvent,
  BankFormField,
  BankFormRequest,
  BankFormTemplate,
  BankMatch,
  BankProduct,
  ClientTask,
  ConstructionStage,
  DemoUser,
  DocumentItem,
  EmploymentType,
  LoanPurpose,
  MatchReason,
  ProductType,
  Role,
  RuleDefinition
} from "./types";

type BootstrapPayload = {
  bankProducts: BankProduct[];
  applications: Application[];
  bankFormTemplates: BankFormTemplate[];
  bankFormRequests: BankFormRequest[];
  clientTasks: ClientTask[];
  rules: RuleDefinition[];
  auditEvents: AuditEvent[];
};

type RegisterInput = {
  fullName: string;
  email: string;
  phone: string;
  city: string;
  password: string;
  purpose: LoanPurpose;
  requestedAmount: number;
  netMonthlyIncome: number;
};

type ProfileInput = {
  name: string;
  title: string;
  description: string;
  phone?: string;
  city?: string;
};

type PasswordInput = {
  currentPassword: string;
  newPassword: string;
};

type SaveStatus = "idle" | "dirty" | "saving" | "saved" | "error";

const statusLabels: Record<ApplicationStatus, string> = {
  draft: "Чернова",
  submitted: "Подадено заявление",
  auto_qualified: "Предварително допустим профил",
  needs_review: "За преглед",
  missing_documents: "Липсват документи",
  bank_routes_selected: "Избрани банкови маршрути",
  bank_form_requested: "Изискана банкова форма",
  bank_form_completed: "Банкова форма попълнена",
  bank_ready: "Готова за банка",
  sent_to_bank: "Изпратена към банка",
  bank_returned: "Банката поиска корекция",
  offer_received: "Получена оферта",
  offer_accepted: "Приета оферта",
  offer_rejected: "Отказана оферта",
  declined_precheck: "Спряна при предварителна оценка",
  archived: "Архивирано"
};

const purposeLabels: Record<LoanPurpose, string> = {
  home_purchase: "Покупка на жилище",
  construction: "Строителство / довършване",
  refinance: "Рефинансиране",
  consumer: "Потребителски кредит",
  business: "Бизнес кредит"
};

const stageLabels: Record<ConstructionStage, string> = {
  act14: "Акт 14",
  act15: "Акт 15",
  act16: "Акт 16",
  existing: "Съществуващ имот",
  none: "Няма обезпечение"
};

const employmentLabels: Record<EmploymentType, string> = {
  permanent_contract: "Трудов договор",
  temporary_contract: "Срочен договор",
  self_employed: "Свободна професия",
  business_owner: "Собствен бизнес",
  civil_contract: "Граждански договор"
};

const roleLabels: Record<Role, string> = {
  client: "Клиент",
  consultant: "Консултант",
  admin: "Администратор"
};

const productTypeLabels: Record<ProductType, string> = {
  mortgage: "Ипотечен кредит",
  consumer: "Потребителски кредит",
  business: "Бизнес кредит",
  refinance: "Рефинансиране"
};

const ruleCategoryLabels: Record<RuleDefinition["category"], string> = {
  eligibility: "Допустимост",
  risk: "Риск",
  documents: "Документи",
  routing: "Насочване"
};

const formRequestStatusLabels: Record<BankFormRequest["status"], string> = {
  requested: "Изискана",
  in_progress: "В процес",
  submitted: "Подадена",
  needs_correction: "За корекция",
  verified: "Проверена",
  cancelled: "Отменена"
};

const taskStatusLabels: Record<ClientTask["status"], string> = {
  open: "Отворена",
  in_progress: "В процес",
  done: "Изпълнена",
  overdue: "Просрочена"
};

const fieldTypeLabels: Record<BankFormField["type"], string> = {
  text: "Текст",
  number: "Число",
  date: "Дата",
  select: "Избор",
  yes_no: "Да/Не",
  checkbox: "Отметка",
  textarea: "Дълъг текст",
  file: "Файл"
};

const severityLabels: Record<RuleDefinition["severity"], string> = {
  info: "Информация",
  warning: "Внимание",
  blocker: "Блокиращо"
};

const mappedFieldLabels: Record<string, string> = {
  "applicant.fullName": "име на клиент",
  "applicant.phone": "телефон",
  "applicant.email": "имейл",
  "applicant.employmentType": "тип заетост",
  "applicant.salaryTransferPossible": "възможен превод на заплата",
  "financial.netMonthlyIncome": "нетен доход",
  "financial.requestedAmount": "искана сума",
  "financial.termYears": "срок",
  "financial.constructionStage": "етап на имот",
  "financial.existingMonthlyDebt": "текущи задължения"
};

const fieldNumber = (value: number) => (Number.isFinite(value) ? value : 0);

const classNames = (...values: Array<string | false | undefined>) => values.filter(Boolean).join(" ");

const formFieldPreview = (field: BankFormField) =>
  [
    fieldTypeLabels[field.type],
    field.required ? "задължително" : "незадължително",
    field.mapsFrom ? `от досие: ${mappedFieldLabels[field.mapsFrom] ?? field.mapsFrom}` : undefined
  ]
    .filter(Boolean)
    .join(" · ");

type NewBankFormTemplateInput = Pick<
  BankFormTemplate,
  "bankName" | "productId" | "title" | "version" | "description" | "fields" | "requiredDocumentIds" | "requiredConsentScope"
>;

const toneIcon = (tone: MatchReason["tone"]) => {
  if (tone === "positive") return <CheckCircle2 size={15} />;
  if (tone === "negative") return <XCircle size={15} />;
  if (tone === "warning") return <AlertTriangle size={15} />;
  return <ChevronRight size={15} />;
};

const cloneBankProducts = () => seededBankProducts.map((product) => structuredClone(product));
const cloneApplications = () => seededApplications.map((application) => structuredClone(application));
const cloneFormTemplates = () => seededBankFormTemplates.map((template) => structuredClone(template));
const cloneFormRequests = () => seededBankFormRequests.map((request) => structuredClone(request));
const cloneClientTasks = () => seededClientTasks.map((task) => structuredClone(task));
const cloneRules = () => seededRuleDefinitions.map((rule) => structuredClone(rule));
const cloneAuditEvents = () => seededAuditEvents.map((event) => structuredClone(event));

const todayIsoDate = () => new Date().toISOString().slice(0, 10);

const emptyBankProduct = (): BankProduct => ({
  id: `product-custom-${Date.now()}`,
  bankName: "Нова банка",
  productName: "Нов кредитен продукт",
  productType: "mortgage",
  isActive: true,
  headline: "Администраторски добавен продукт за предварително насочване.",
  minRate: 3.2,
  maxRate: 4.7,
  aprFrom: 4.1,
  maxAmount: 250000,
  maxTermYears: 30,
  maxLtv: 0.8,
  maxDti: 0.5,
  minIncome: 1600,
  minEmploymentMonths: 6,
  acceptedStages: ["act16", "existing"],
  requiresSalaryTransfer: false,
  supportsOnlineFlow: true,
  strengths: ["Дигитален предварителен процес"],
  cautions: ["Нужна е проверка на актуалните банкови условия"],
  sourceUrl: "https://mg-credit.pages.dev/",
  sourceLabel: "Вътрешен продуктов каталог",
  lastChecked: todayIsoDate()
});

const emptyRuleDefinition = (): RuleDefinition => ({
  id: `rule-custom-${Date.now()}`,
  name: "Ново вътрешно правило",
  category: "eligibility",
  description: "Описание на правилото и защо е необходимо.",
  condition: "Кога системата трябва да приложи това правило.",
  effect: "Какво действие или предупреждение се създава.",
  version: "2026.01",
  severity: "warning",
  active: true,
  updatedAt: new Date().toISOString()
});

const emptyBankFormField = (): BankFormField => ({
  id: `field_${Date.now()}`,
  label: "Ново поле",
  type: "text",
  required: true,
  section: "Основни данни",
  mapsFrom: "applicant.fullName"
});

const readMappedValue = (application: Application, path?: string) => {
  if (!path) return "";
  const [scope, key] = path.split(".");
  if (scope === "applicant") return application.applicant[key as keyof Application["applicant"]] ?? "";
  if (scope === "financial") return application.financial[key as keyof Application["financial"]] ?? "";
  return "";
};

const prefillFormValues = (template: BankFormTemplate, application: Application) => {
  return template.fields.reduce<Record<string, string | number | boolean>>((values, field) => {
    const mapped = readMappedValue(application, field.mapsFrom);
    if (mapped !== "") {
      values[field.id] = mapped as string | number | boolean;
    }
    return values;
  }, {});
};

function App() {
  const [currentUser, setCurrentUser] = useState<DemoUser | null>(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [authBusy, setAuthBusy] = useState(false);
  const [authError, setAuthError] = useState("");
  const [dataLoading, setDataLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState<SaveStatus>("idle");
  const [saveMessage, setSaveMessage] = useState("");
  const [activeView, setActiveView] = useState("overview");
  const [bankProducts, setBankProducts] = useState<BankProduct[]>(cloneBankProducts);
  const [applications, setApplications] = useState<Application[]>(cloneApplications);
  const [bankFormTemplates, setBankFormTemplates] = useState<BankFormTemplate[]>(cloneFormTemplates);
  const [bankFormRequests, setBankFormRequests] = useState<BankFormRequest[]>(cloneFormRequests);
  const [clientTasks, setClientTasks] = useState<ClientTask[]>(cloneClientTasks);
  const [ruleDefinitions, setRuleDefinitions] = useState<RuleDefinition[]>(cloneRules);
  const [auditEvents, setAuditEvents] = useState<AuditEvent[]>(cloneAuditEvents);
  const [selectedApplicationId, setSelectedApplicationId] = useState("app-1001");
  const [searchTerm, setSearchTerm] = useState("");

  const activateUser = (user: DemoUser) => {
    setCurrentUser(user);
    setActiveView(defaultView(user.role));
    if (user.linkedApplicationId != null) {
      setSelectedApplicationId(user.linkedApplicationId);
    }
  };

  const applyBootstrapPayload = (payload: BootstrapPayload) => {
    setBankProducts(payload.bankProducts);
    setApplications(payload.applications);
    setBankFormTemplates(payload.bankFormTemplates);
    setBankFormRequests(payload.bankFormRequests);
    setClientTasks(payload.clientTasks);
    setRuleDefinitions(payload.rules);
    setAuditEvents(payload.auditEvents);
  };

  const upsertApplicationState = (nextApplication: Application) => {
    setApplications((items) => {
      const exists = items.some((application) => application.id === nextApplication.id);
      if (!exists) return [nextApplication, ...items];
      return items.map((application) => (application.id === nextApplication.id ? nextApplication : application));
    });
  };

  const loadBootstrap = async () => {
    setDataLoading(true);
    try {
      const response = await fetch("/api/bootstrap", { credentials: "include" });
      if (response.status === 401) {
        setCurrentUser(null);
        return;
      }
      if (!response.ok) throw new Error("Неуспешно зареждане на данните.");
      const payload = (await response.json()) as BootstrapPayload;
      applyBootstrapPayload(payload);
    } catch (error) {
      console.warn(error);
    } finally {
      setDataLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const loadSession = async () => {
      try {
        const response = await fetch("/api/session", { credentials: "include" });
        if (!response.ok) return;
        const payload = (await response.json()) as { authenticated: boolean; user?: DemoUser };
        if (!cancelled && payload.authenticated && payload.user) {
          activateUser(payload.user);
        }
      } catch (error) {
        console.warn(error);
      } finally {
        if (!cancelled) setAuthLoading(false);
      }
    };

    void loadSession();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (currentUser) {
      void loadBootstrap();
    }
  }, [currentUser?.id]);

  const login = async (email: string, password: string) => {
    setAuthBusy(true);
    setAuthError("");
    try {
      const response = await fetch("/api/login", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email, password })
      });
      const payload = (await response.json()) as { authenticated?: boolean; user?: DemoUser; error?: string };
      if (!response.ok || !payload.authenticated || !payload.user) {
        setAuthError(payload.error ?? "Неуспешен вход.");
        return;
      }
      activateUser(payload.user);
    } catch {
      setAuthError("Входът не може да се изпълни в момента.");
    } finally {
      setAuthBusy(false);
    }
  };

  const register = async (input: RegisterInput) => {
    setAuthBusy(true);
    setAuthError("");
    try {
      const response = await fetch("/api/register", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input)
      });
      const payload = (await response.json()) as {
        authenticated?: boolean;
        user?: DemoUser;
        application?: Application;
        error?: string;
      };
      if (!response.ok || !payload.authenticated || !payload.user) {
        setAuthError(payload.error ?? "Неуспешна регистрация.");
        return;
      }
      if (payload.application) {
        upsertApplicationState(payload.application);
        setSelectedApplicationId(payload.application.id);
      }
      activateUser(payload.user);
    } catch {
      setAuthError("Регистрацията не може да се изпълни в момента.");
    } finally {
      setAuthBusy(false);
    }
  };

  const logout = async () => {
    await fetch("/api/logout", { method: "POST", credentials: "include" }).catch(() => undefined);
    setCurrentUser(null);
    setActiveView("overview");
    setSearchTerm("");
  };

  const selectedApplication = applications.find((application) => application.id === selectedApplicationId) ?? applications[0];
  const clientApplication =
    currentUser?.linkedApplicationId != null
      ? applications.find((application) => application.id === currentUser.linkedApplicationId) ?? selectedApplication
      : selectedApplication;
  const score = useMemo(() => scoreApplication(selectedApplication, bankProducts), [selectedApplication, bankProducts]);
  const clientScore = useMemo(() => scoreApplication(clientApplication, bankProducts), [clientApplication, bankProducts]);
  const selectedRequests = bankFormRequests.filter((request) => request.applicationId === selectedApplication.id);
  const clientRequests = bankFormRequests.filter((request) => request.applicationId === clientApplication.id);
  const clientVisibleTasks = clientTasks.filter((task) => task.applicationId === clientApplication.id);

  const setClientApplication = (updater: (application: Application) => Application) => {
    setApplications((items) =>
      items.map((application) =>
        application.id === clientApplication.id
          ? { ...updater(application), updatedAt: new Date().toISOString() }
          : application
      )
    );
    setSaveStatus("dirty");
    setSaveMessage("Има незапазени промени.");
  };

  const persistApplication = async (application: Application, successMessage = "Промените са запазени.") => {
    setSaveStatus("saving");
    setSaveMessage("Записване към защитената база...");
    try {
      const response = await fetch(`/api/applications/${encodeURIComponent(application.id)}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ application })
      });
      const payload = (await response.json()) as { application?: Application; error?: string };
      if (!response.ok || !payload.application) {
        throw new Error(payload.error ?? "Записът не беше приет.");
      }
      upsertApplicationState(payload.application);
      setSaveStatus("saved");
      setSaveMessage(successMessage);
      return payload.application;
    } catch (error) {
      setSaveStatus("error");
      setSaveMessage(error instanceof Error ? error.message : "Неуспешен запис.");
      return null;
    }
  };

  const updateFinancial = (field: keyof Application["financial"], value: string | number) => {
    setClientApplication((application) => ({
      ...application,
      financial: {
        ...application.financial,
        [field]: typeof value === "number" ? fieldNumber(value) : value
      }
    }));
  };

  const updateApplicant = (field: keyof Application["applicant"], value: string | number | boolean) => {
    setClientApplication((application) => ({
      ...application,
      applicant: {
        ...application.applicant,
        [field]: value
      }
    }));
  };

  const toggleConsent = (field: keyof Application["consents"]) => {
    if (field === "acceptedAt" || field === "ipAddress" || field === "documentVersion") return;
    setClientApplication((application) => ({
      ...application,
      consents: {
        ...application.consents,
        [field]: !application.consents[field],
        acceptedAt: new Date().toISOString()
      }
    }));
  };

  const updateDocumentStatus = (documentId: string, status: DocumentItem["status"]) => {
    setClientApplication((application) => ({
      ...application,
      documents: application.documents.map((document) => (document.id === documentId ? { ...document, status } : document))
    }));
  };

  const changeApplicationStatus = (applicationId: string, status: ApplicationStatus) => {
    const target = applications.find((application) => application.id === applicationId);
    if (!target) return;
    const nextApplication = { ...target, status, updatedAt: new Date().toISOString() };
    setApplications((items) =>
      items.map((application) =>
        application.id === applicationId ? nextApplication : application
      )
    );
    void persistApplication(nextApplication, "Статусът е запазен.");
  };

  const createBankFormRequest = async (applicationId: string, templateId: string, consultantNote: string) => {
    const template = bankFormTemplates.find((item) => item.id === templateId);
    const application = applications.find((item) => item.id === applicationId);
    if (!template || !application) return;

    const requestId = `req-${template.id}-${Date.now()}`;
    const dueDate = new Date(Date.now() + 3 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
    const request: BankFormRequest = {
      id: requestId,
      applicationId,
      templateId,
      requestedBy: currentUser?.id ?? "user-consultant-1",
      assignedToUserId: application.clientUserId ?? "user-client-1",
      status: "requested",
      dueDate,
      requestedAt: new Date().toISOString(),
      consultantNote,
      values: prefillFormValues(template, application),
      correctionNotes: []
    };
    let savedRequest = request;

    try {
      const response = await fetch("/api/bank-forms/requests", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(request)
      });
      const payload = (await response.json()) as { request?: BankFormRequest };
      if (response.ok && payload.request) {
        savedRequest = payload.request;
      }
    } catch {
      // The local optimistic state still keeps the demo usable if the remote write fails.
    }

    setBankFormRequests((items) => [savedRequest, ...items]);
    setClientTasks((items) => [
      {
        id: `task-${savedRequest.id}`,
        applicationId,
        type: "bank_form",
        title: `Попълнете банкова форма: ${template.bankName}`,
        description: `${template.title}, версия ${template.version}. ${consultantNote}`,
        status: "open",
        dueDate,
        relatedRequestId: savedRequest.id,
        createdAt: new Date().toISOString()
      },
      ...items
    ]);
    changeApplicationStatus(applicationId, "bank_form_requested");
  };

  const updateBankFormValue = (requestId: string, fieldId: string, value: string | number | boolean) => {
    const request = bankFormRequests.find((item) => item.id === requestId);
    setBankFormRequests((items) =>
      items.map((request) =>
        request.id === requestId
          ? {
              ...request,
              status: request.status === "requested" ? "in_progress" : request.status,
              values: { ...request.values, [fieldId]: value }
            }
          : request
      )
    );
    setClientTasks((items) =>
      items.map((task) =>
        task.relatedRequestId === requestId && task.status === "open" ? { ...task, status: "in_progress" } : task
      )
    );
    if (request) {
      void fetch(`/api/bank-forms/requests/${encodeURIComponent(requestId)}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ status: request.status === "requested" ? "in_progress" : request.status, values: { [fieldId]: value } })
      }).catch(() => undefined);
    }
  };

  const submitBankFormRequest = async (requestId: string) => {
    const request = bankFormRequests.find((item) => item.id === requestId);
    if (!request) return;
    let savedRequest: BankFormRequest | null = null;
    try {
      const response = await fetch(`/api/bank-forms/requests/${encodeURIComponent(requestId)}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ status: "submitted", values: request.values })
      });
      const payload = (await response.json()) as { request?: BankFormRequest };
      if (response.ok && payload.request) savedRequest = payload.request;
    } catch {
      savedRequest = null;
    }
    setBankFormRequests((items) =>
      items.map((item) =>
        item.id === requestId ? savedRequest ?? { ...item, status: "submitted", submittedAt: new Date().toISOString() } : item
      )
    );
    setClientTasks((items) =>
      items.map((task) => (task.relatedRequestId === requestId ? { ...task, status: "done" } : task))
    );
    changeApplicationStatus(request.applicationId, "bank_form_completed");
  };

  const verifyBankFormRequest = async (requestId: string) => {
    const request = bankFormRequests.find((item) => item.id === requestId);
    if (!request) return;
    let savedRequest: BankFormRequest | null = null;
    try {
      const response = await fetch(`/api/bank-forms/requests/${encodeURIComponent(requestId)}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ status: "verified" })
      });
      const payload = (await response.json()) as { request?: BankFormRequest };
      if (response.ok && payload.request) savedRequest = payload.request;
    } catch {
      savedRequest = null;
    }
    setBankFormRequests((items) => items.map((item) => (item.id === requestId ? savedRequest ?? { ...item, status: "verified" } : item)));
    changeApplicationStatus(request.applicationId, "bank_ready");
  };

  const createBankFormTemplate = async (input: NewBankFormTemplateInput) => {
    const template: BankFormTemplate = {
      id: `form-custom-${Date.now()}`,
      bankName: input.bankName,
      productId: input.productId,
      title: input.title,
      version: input.version,
      isActive: true,
      description: input.description,
      requiredConsentScope: input.requiredConsentScope,
      requiredDocumentIds: input.requiredDocumentIds,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      fields: input.fields
    };
    try {
      const response = await fetch("/api/bank-forms/templates", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(template)
      });
      const payload = (await response.json()) as { template?: BankFormTemplate };
      setBankFormTemplates((items) => [payload.template ?? template, ...items]);
    } catch {
      setBankFormTemplates((items) => [template, ...items]);
    }
  };

  const updateBankFormTemplate = async (template: BankFormTemplate) => {
    try {
      const response = await fetch(`/api/bank-forms/templates/${encodeURIComponent(template.id)}`, {
        method: "PATCH",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ template })
      });
      const payload = (await response.json()) as { template?: BankFormTemplate };
      const savedTemplate = response.ok && payload.template ? payload.template : { ...template, updatedAt: new Date().toISOString() };
      setBankFormTemplates((items) => items.map((item) => (item.id === savedTemplate.id ? savedTemplate : item)));
      return savedTemplate;
    } catch {
      setBankFormTemplates((items) => items.map((item) => (item.id === template.id ? template : item)));
      return template;
    }
  };

  const saveBankProduct = async (product: BankProduct) => {
    const isExisting = bankProducts.some((item) => item.id === product.id);
    try {
      const response = await fetch(isExisting ? `/api/admin/products/${encodeURIComponent(product.id)}` : "/api/admin/products", {
        method: isExisting ? "PATCH" : "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ product })
      });
      const payload = (await response.json()) as { product?: BankProduct; error?: string };
      if (!response.ok || !payload.product) throw new Error(payload.error ?? "Продуктът не беше записан.");
      const savedProduct = payload.product;
      setBankProducts((items) => {
        const exists = items.some((item) => item.id === savedProduct.id);
        return exists
          ? items.map((item) => (item.id === savedProduct.id ? savedProduct : item))
          : [savedProduct, ...items];
      });
      return savedProduct;
    } catch (error) {
      console.warn(error);
      return null;
    }
  };

  const archiveBankProduct = async (productId: string) => {
    const product = bankProducts.find((item) => item.id === productId);
    if (!product) return null;
    const nextProduct = { ...product, isActive: false, archivedAt: new Date().toISOString(), updatedAt: new Date().toISOString() };
    try {
      const response = await fetch(`/api/admin/products/${encodeURIComponent(productId)}`, {
        method: "DELETE",
        credentials: "include"
      });
      const payload = (await response.json()) as { product?: BankProduct };
      const archived = response.ok && payload.product ? payload.product : nextProduct;
      setBankProducts((items) => items.map((item) => (item.id === archived.id ? archived : item)));
      return archived;
    } catch {
      setBankProducts((items) => items.map((item) => (item.id === productId ? nextProduct : item)));
      return nextProduct;
    }
  };

  const saveRuleDefinition = async (rule: RuleDefinition) => {
    const isExisting = ruleDefinitions.some((item) => item.id === rule.id);
    try {
      const response = await fetch(isExisting ? `/api/admin/rules/${encodeURIComponent(rule.id)}` : "/api/admin/rules", {
        method: isExisting ? "PATCH" : "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ rule })
      });
      const payload = (await response.json()) as { rule?: RuleDefinition; error?: string };
      if (!response.ok || !payload.rule) throw new Error(payload.error ?? "Правилото не беше записано.");
      const savedRule = payload.rule;
      setRuleDefinitions((items) => {
        const exists = items.some((item) => item.id === savedRule.id);
        return exists
          ? items.map((item) => (item.id === savedRule.id ? savedRule : item))
          : [savedRule, ...items];
      });
      return savedRule;
    } catch (error) {
      console.warn(error);
      return null;
    }
  };

  const updateProfile = async (input: ProfileInput) => {
    try {
      const response = await fetch("/api/profile", {
        method: "PATCH",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input)
      });
      const payload = (await response.json()) as { user?: DemoUser; application?: Application; error?: string };
      if (!response.ok || !payload.user) throw new Error(payload.error ?? "Профилът не беше записан.");
      setCurrentUser(payload.user);
      if (payload.application) upsertApplicationState(payload.application);
      return { ok: true, message: "Профилът е запазен." };
    } catch (error) {
      return { ok: false, message: error instanceof Error ? error.message : "Профилът не беше записан." };
    }
  };

  const changePassword = async (input: PasswordInput) => {
    try {
      const response = await fetch("/api/profile/password", {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(input)
      });
      const payload = (await response.json()) as { ok?: boolean; error?: string };
      if (!response.ok || !payload.ok) throw new Error(payload.error ?? "Паролата не беше сменена.");
      return { ok: true, message: "Паролата е сменена. Другите сесии са прекратени." };
    } catch (error) {
      return { ok: false, message: error instanceof Error ? error.message : "Паролата не беше сменена." };
    }
  };

  const downloadCasePack = (application: Application) => {
    const result = scoreApplication(application, bankProducts);
    const payload = {
      generatedAt: new Date().toISOString(),
      demoOnly: true,
      application,
      score: result.score,
      risk: riskLabel[result.risk],
      route: routeLabel[result.autoRoute],
      topBankMatches: result.bankMatches.slice(0, 5).map((match) => ({
        bank: match.product.bankName,
        product: match.product.productName,
        status: match.status,
        fitScore: match.fitScore,
        estimatedPayment: Math.round(match.estimatedPayment),
        reasons: match.reasons.map((item) => item.label)
      }))
    };

    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${application.id}-bank-package-demo.json`;
    link.click();
    URL.revokeObjectURL(url);
  };

  if (authLoading) {
    return <LoadingScreen />;
  }

  if (currentUser == null) {
    return <LoginScreen onLogin={login} onRegister={register} loading={authBusy} error={authError} />;
  }

  const navItems = getNavigation(currentUser.role);

  return (
    <div className="app-shell">
      <aside className="sidebar">
        <div className="brand-block">
          <div className="brand-mark">MG</div>
          <div>
            <strong>MG Credit</strong>
            <span>демонстрационен портал</span>
          </div>
        </div>

        <nav className="nav-list" aria-label="Основна навигация">
          {navItems.map((item) => (
            <button
              key={item.id}
              className={classNames("nav-item", activeView === item.id && "active")}
              type="button"
              onClick={() => setActiveView(item.id)}
            >
              <item.icon size={18} />
              <span>{item.label}</span>
            </button>
          ))}
        </nav>

        <div className="sidebar-footer">
          <div className="security-note">
            <ShieldCheck size={17} />
            <span>Демонстрационен режим: фиктивни клиенти и контролирани примерни данни.</span>
          </div>
        </div>
      </aside>

      <main className="main-area">
        <header className="topbar">
          <div>
            <div className="eyebrow">Вътрешна система за предварителна кредитна оценка</div>
            <h1>{viewTitle(activeView, currentUser.role)}</h1>
          </div>
          <div className="topbar-actions">
            <span className="api-pill">
              <LockKeyhole size={15} />
              Реална сесия
            </span>
            <UserBadge currentUser={currentUser} />
            <button className="icon-button" type="button" aria-label="Изход" onClick={logout}>
              <LogOut size={19} />
            </button>
          </div>
        </header>

        {dataLoading && (
          <div className="inline-loader">
            <ShieldCheck size={17} />
            <span>Зареждане на защитени данни...</span>
          </div>
        )}

        {activeView === "profile" && (
          <ProfileView
            currentUser={currentUser}
            application={currentUser.role === "client" ? clientApplication : undefined}
            updateProfile={updateProfile}
            changePassword={changePassword}
          />
        )}

        {currentUser.role === "client" && (
          <>
            {activeView === "overview" && <ClientOverview application={clientApplication} score={clientScore} />}
            {activeView === "application" && (
              <ClientApplicationForm
                application={clientApplication}
                score={clientScore}
                updateApplicant={updateApplicant}
                updateFinancial={updateFinancial}
                saveStatus={saveStatus}
                saveMessage={saveMessage}
                onSave={() => persistApplication(clientApplication)}
              />
            )}
            {activeView === "results" && <ResultsView application={clientApplication} score={clientScore} />}
            {activeView === "documents" && (
              <DocumentsView
                application={clientApplication}
                score={clientScore}
                toggleConsent={toggleConsent}
                updateDocumentStatus={updateDocumentStatus}
                downloadCasePack={downloadCasePack}
                saveStatus={saveStatus}
                saveMessage={saveMessage}
                onSave={() => persistApplication(clientApplication, "Документите и съгласията са запазени.")}
              />
            )}
            {activeView === "bankForms" && (
              <ClientBankFormsView
                application={clientApplication}
                tasks={clientVisibleTasks}
                requests={clientRequests}
                templates={bankFormTemplates}
                updateBankFormValue={updateBankFormValue}
                submitBankFormRequest={submitBankFormRequest}
              />
            )}
          </>
        )}

        {currentUser.role === "consultant" && (
          <>
            {activeView === "pipeline" && (
              <ConsultantPipeline
                applications={applications}
                products={bankProducts}
                selectedApplicationId={selectedApplicationId}
                searchTerm={searchTerm}
                setSearchTerm={setSearchTerm}
                onSelect={(id) => {
                  setSelectedApplicationId(id);
                  setActiveView("case");
                }}
              />
            )}
            {activeView === "case" && (
              <ConsultantCase
                application={selectedApplication}
                score={score}
                formRequests={selectedRequests}
                formTemplates={bankFormTemplates}
                onStatusChange={changeApplicationStatus}
                onRequestBankForm={createBankFormRequest}
                onVerifyBankForm={verifyBankFormRequest}
                downloadCasePack={downloadCasePack}
              />
            )}
            {activeView === "messages" && <MessagesView applications={applications} products={bankProducts} />}
          </>
        )}

        {currentUser.role === "admin" && (
          <>
            {activeView === "products" && (
              <ProductsAdmin
                products={bankProducts}
                saveProduct={saveBankProduct}
                archiveProduct={archiveBankProduct}
              />
            )}
            {activeView === "forms" && (
              <BankFormsAdmin
                templates={bankFormTemplates}
                products={bankProducts}
                createBankFormTemplate={createBankFormTemplate}
                updateBankFormTemplate={updateBankFormTemplate}
              />
            )}
            {activeView === "rules" && <RulesAdmin rules={ruleDefinitions} saveRule={saveRuleDefinition} />}
            {activeView === "audit" && <AuditView events={auditEvents} />}
            {activeView === "production" && <ProductionView />}
          </>
        )}
      </main>
    </div>
  );
}

function getNavigation(role: Role) {
  if (role === "client") {
    return [
      { id: "overview", label: "Начало", icon: LayoutDashboard },
      { id: "application", label: "Заявка", icon: PencilLine },
      { id: "results", label: "Оценка", icon: Gauge },
      { id: "documents", label: "Документи", icon: FileCheck2 },
      { id: "bankForms", label: "Банкови форми", icon: FileText },
      { id: "profile", label: "Профил", icon: UserCog }
    ];
  }

  if (role === "consultant") {
    return [
      { id: "pipeline", label: "Заявки", icon: ClipboardCheck },
      { id: "case", label: "Досие", icon: FileText },
      { id: "messages", label: "Комуникация", icon: MessageSquareText },
      { id: "profile", label: "Профил", icon: UserCog }
    ];
  }

  return [
    { id: "products", label: "Банкови продукти", icon: Landmark },
    { id: "forms", label: "Банкови форми", icon: FilePlus2 },
    { id: "rules", label: "Правила", icon: SlidersHorizontal },
    { id: "audit", label: "Журнал", icon: Workflow },
    { id: "production", label: "Продукционна готовност", icon: Settings2 },
    { id: "profile", label: "Профил", icon: UserCog }
  ];
}

function defaultView(role: Role) {
  if (role === "client") return "overview";
  if (role === "consultant") return "pipeline";
  return "products";
}

function viewTitle(view: string, role: Role) {
  const titles: Record<string, string> = {
    overview: "Клиентски портал",
    application: "Кредитна заявка",
    results: "Автоматична оценка",
    documents: "Документи и съгласия",
    bankForms: "Банкови форми за попълване",
    pipeline: "Консултантски списък със заявления",
    case: "Детайл на досие",
    messages: "Комуникация",
    products: "Продуктов каталог",
    forms: "Управление на банкови форми",
    rules: "Вътрешни правила",
    audit: "Проследимост",
    production: "Продукционна готовност",
    profile: "Профил и сигурност"
  };
  return titles[view] ?? roleLabels[role];
}

const demoCredentials = [
  { email: "client@mg-credit.demo", password: "Client-2026!", label: "Клиент" },
  { email: "consultant@mg-credit.demo", password: "Consultant-2026!", label: "Консултант" },
  { email: "admin@mg-credit.demo", password: "Admin-2026!", label: "Администратор" }
];

function LoadingScreen() {
  return (
    <main className="login-screen">
      <section className="login-hero">
        <div className="brand-block large">
          <div className="brand-mark">MG</div>
          <div>
            <strong>MG Credit</strong>
            <span>защитен портал</span>
          </div>
        </div>
        <h1>Проверка на сесията.</h1>
        <p>Системата проверява дали има валиден вход и защитена сесия.</p>
      </section>
      <section className="login-panel">
        <div className="inline-loader large">
          <ShieldCheck size={18} />
          <span>Моля, изчакайте...</span>
        </div>
      </section>
    </main>
  );
}

function LoginScreen({
  onLogin,
  onRegister,
  loading,
  error
}: {
  onLogin: (email: string, password: string) => void;
  onRegister: (input: RegisterInput) => void;
  loading: boolean;
  error: string;
}) {
  const [mode, setMode] = useState<"login" | "register">("login");
  const [email, setEmail] = useState("client@mg-credit.demo");
  const [password, setPassword] = useState("Client-2026!");
  const [fullName, setFullName] = useState("Нов клиент");
  const [phone, setPhone] = useState("+359 ");
  const [city, setCity] = useState("София");
  const [purpose, setPurpose] = useState<LoanPurpose>("home_purchase");
  const [requestedAmount, setRequestedAmount] = useState(120000);
  const [netMonthlyIncome, setNetMonthlyIncome] = useState(2200);

  const submitLogin = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onLogin(email, password);
  };

  const submitRegistration = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onRegister({
      fullName,
      email,
      phone,
      city,
      password,
      purpose,
      requestedAmount,
      netMonthlyIncome
    });
  };

  return (
    <main className="login-screen">
      <section className="login-hero">
        <div className="brand-block large">
          <div className="brand-mark">MG</div>
          <div>
            <strong>MG Credit</strong>
            <span>предварителна оценка</span>
          </div>
        </div>
        <h1>Защитен портал за кредитно насочване без излишни срещи.</h1>
        <p>
          Входът вече създава реална сървърна сесия. Ролята идва от потребителя в базата, а защитените данни
          се зареждат след успешна автентикация.
        </p>
        <div className="login-metrics">
          <Metric label="Примерни продукта" value="15" />
          <Metric label="Проверки по правила" value="20+" />
          <Metric label="Роли" value="3" />
        </div>
      </section>

      <section className="login-panel">
        <div className="section-heading">
          <span className="eyebrow">{mode === "login" ? "Вход в системата" : "Нова клиентска регистрация"}</span>
          <h2>{mode === "login" ? "Имейл и парола" : "Клиентски профил"}</h2>
        </div>

        <div className="auth-mode-tabs">
          <button className={classNames(mode === "login" && "active")} type="button" onClick={() => setMode("login")}>
            <LockKeyhole size={16} />
            Вход
          </button>
          <button
            className={classNames(mode === "register" && "active")}
            type="button"
            onClick={() => {
              setMode("register");
              if (email.endsWith("@mg-credit.demo")) {
                setEmail(`client.${Date.now().toString().slice(-5)}@example.test`);
                setPassword("Client-2026!");
              }
            }}
          >
            <UserPlus size={16} />
            Регистрация
          </button>
        </div>

        {mode === "login" ? (
          <>
            <form className="login-form" onSubmit={submitLogin}>
              <TextField label="Имейл" value={email} onChange={setEmail} />
              <label className="field">
                <span>Парола</span>
                <input type="password" value={password} onChange={(event) => setPassword(event.target.value)} />
              </label>
              {error && <div className="auth-error">{error}</div>}
              <button className="primary-button" type="submit" disabled={loading}>
                <LockKeyhole size={17} />
                {loading ? "Влизане..." : "Вход"}
              </button>
            </form>

            <div className="credential-list" aria-label="Тестови потребители">
              {demoCredentials.map((credential) => (
                <button
                  key={credential.email}
                  className="credential-button"
                  type="button"
                  onClick={() => {
                    setEmail(credential.email);
                    setPassword(credential.password);
                  }}
                >
                  <span>{credential.label}</span>
                  <strong>{credential.email}</strong>
                </button>
              ))}
            </div>
          </>
        ) : (
          <form className="login-form" onSubmit={submitRegistration}>
            <TextField label="Име и фамилия" value={fullName} onChange={setFullName} />
            <TextField label="Имейл" value={email} onChange={setEmail} />
            <label className="field">
              <span>Парола</span>
              <input type="password" value={password} onChange={(event) => setPassword(event.target.value)} />
            </label>
            <TextField label="Телефон" value={phone} onChange={setPhone} />
            <TextField label="Град" value={city} onChange={setCity} />
            <SelectField label="Цел" value={purpose} options={purposeLabels} onChange={setPurpose} />
            <NumberField label="Искана сума" value={requestedAmount} onChange={setRequestedAmount} />
            <NumberField label="Нетен месечен доход" value={netMonthlyIncome} onChange={setNetMonthlyIncome} />
            {error && <div className="auth-error">{error}</div>}
            <button className="primary-button" type="submit" disabled={loading}>
              <UserPlus size={17} />
              {loading ? "Създаване..." : "Създай клиентски профил"}
            </button>
          </form>
        )}

        <p className="demo-disclaimer">
          Това не е реална кредитна оферта. Входът е реална session автентикация за демонстрационни потребители;
          преди продукционен старт паролите трябва да се заменят с клиентска регистрация, MFA и политики за достъп.
        </p>
      </section>
    </main>
  );
}

function UserBadge({ currentUser }: { currentUser: DemoUser }) {
  return (
    <div className="user-badge">
      <span>{roleLabels[currentUser.role]}</span>
      <strong>{currentUser.name}</strong>
    </div>
  );
}

function Metric({ label, value }: { label: string; value: string }) {
  return (
    <div className="metric">
      <strong>{value}</strong>
      <span>{label}</span>
    </div>
  );
}

function SaveControl({ status, message, onSave }: { status: SaveStatus; message: string; onSave: () => void }) {
  const label =
    status === "saving"
      ? "Записване..."
      : status === "saved"
        ? "Запазено"
        : status === "error"
          ? "Повтори запис"
          : "Запази";

  return (
    <div className="save-control">
      <span className={classNames("save-state", status)}>{message || "Промените се пазят в защитена база."}</span>
      <button className="secondary-button" type="button" onClick={onSave} disabled={status === "saving"}>
        <CheckCircle2 size={16} />
        {label}
      </button>
    </div>
  );
}

function ProfileView({
  currentUser,
  application,
  updateProfile,
  changePassword
}: {
  currentUser: DemoUser;
  application?: Application;
  updateProfile: (input: ProfileInput) => Promise<{ ok: boolean; message: string }>;
  changePassword: (input: PasswordInput) => Promise<{ ok: boolean; message: string }>;
}) {
  const [name, setName] = useState(currentUser.name);
  const [title, setTitle] = useState(currentUser.title);
  const [description, setDescription] = useState(currentUser.description);
  const [phone, setPhone] = useState(application?.applicant.phone ?? "");
  const [city, setCity] = useState(application?.applicant.city ?? "");
  const [profileStatus, setProfileStatus] = useState<{ ok: boolean; message: string } | null>(null);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [repeatPassword, setRepeatPassword] = useState("");
  const [passwordStatus, setPasswordStatus] = useState<{ ok: boolean; message: string } | null>(null);
  const [busy, setBusy] = useState<"profile" | "password" | null>(null);

  const submitProfile = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setBusy("profile");
    const result = await updateProfile({ name, title, description, phone, city });
    setProfileStatus(result);
    setBusy(null);
  };

  const submitPassword = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (newPassword !== repeatPassword) {
      setPasswordStatus({ ok: false, message: "Новата парола и повторението не съвпадат." });
      return;
    }
    setBusy("password");
    const result = await changePassword({ currentPassword, newPassword });
    setPasswordStatus(result);
    if (result.ok) {
      setCurrentPassword("");
      setNewPassword("");
      setRepeatPassword("");
    }
    setBusy(null);
  };

  return (
    <div className="two-column-layout profile-layout">
      <section className="panel">
        <div className="panel-header">
          <div>
            <span className="eyebrow">Профил</span>
            <h2>Данни за потребителя</h2>
          </div>
          <span className="status-pill verified">{roleLabels[currentUser.role]}</span>
        </div>
        <form className="admin-form-grid profile-form-grid" onSubmit={submitProfile}>
          <TextField label="Име" value={name} onChange={setName} />
          <TextField label="Роля в системата" value={title} onChange={setTitle} />
          <label className="field">
            <span>Имейл</span>
            <input value={currentUser.email} disabled />
          </label>
          {application && <TextField label="Телефон" value={phone} onChange={setPhone} />}
          {application && <TextField label="Град" value={city} onChange={setCity} />}
          <label className="field wide-field">
            <span>Описание</span>
            <textarea value={description} onChange={(event) => setDescription(event.target.value)} />
          </label>
          <div className="wide-field button-row">
            <button className="primary-button" type="submit" disabled={busy === "profile"}>
              <Save size={17} />
              {busy === "profile" ? "Записване..." : "Запази профил"}
            </button>
            {profileStatus && (
              <span className={classNames("status-message", profileStatus.ok ? "success" : "error")}>
                {profileStatus.message}
              </span>
            )}
          </div>
        </form>
      </section>

      <section className="panel">
        <div className="panel-header">
          <div>
            <span className="eyebrow">Сигурност</span>
            <h2>Смяна на парола</h2>
          </div>
          <span className="pill muted">Активна сесия</span>
        </div>
        <form className="login-form security-form" onSubmit={submitPassword}>
          <label className="field">
            <span>Текуща парола</span>
            <input type="password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} />
          </label>
          <label className="field">
            <span>Нова парола</span>
            <input type="password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} />
          </label>
          <label className="field">
            <span>Повторете новата парола</span>
            <input type="password" value={repeatPassword} onChange={(event) => setRepeatPassword(event.target.value)} />
          </label>
          <button className="secondary-button" type="submit" disabled={busy === "password"}>
            <LockKeyhole size={17} />
            {busy === "password" ? "Смяна..." : "Смени парола"}
          </button>
          {passwordStatus && (
            <span className={classNames("status-message", passwordStatus.ok ? "success" : "error")}>
              {passwordStatus.message}
            </span>
          )}
        </form>
      </section>

      <section className="panel sticky-panel">
        <div className="section-heading">
          <span className="eyebrow">Контрол на достъп</span>
          <h2>Профилът е част от audit trail-а</h2>
        </div>
        <div className="trace-list">
          <TraceItem item={{ label: "Имейлът остава read-only до въвеждане на потвърждение по имейл.", tone: "neutral" }} />
          <TraceItem item={{ label: "Смяната на парола прекратява другите активни сесии.", tone: "positive" }} />
          <TraceItem item={{ label: "Клиентският контакт се синхронизира със свързаната кредитна заявка.", tone: application ? "positive" : "neutral" }} />
        </div>
      </section>
    </div>
  );
}

function ClientOverview({ application, score }: { application: Application; score: ReturnType<typeof scoreApplication> }) {
  const topMatches = score.bankMatches.filter((match) => match.status !== "blocked").slice(0, 3);

  return (
    <div className="content-stack">
      <section className="summary-grid">
        <ScorePanel score={score} />
        <div className="panel action-panel">
          <div className="section-heading">
            <span className="eyebrow">Следващо действие</span>
            <h2>{routeLabel[score.autoRoute]}</h2>
          </div>
          <p>
            {score.autoRoute === "request_documents"
              ? "Качи липсващите документи, за да може пакетът да се изпрати към банка."
              : score.autoRoute === "auto_to_bank"
                ? "Профилът е достатъчно чист за директно банково насочване."
                : "Случаят има нужда от експертен преглед преди изпращане."}
          </p>
          <div className="progress-row">
            <span>Попълване</span>
            <div className="progress-track">
              <div style={{ width: `${Math.min(100, score.score)}%` }} />
            </div>
            <strong>{score.score}%</strong>
          </div>
        </div>
        <div className="panel">
          <div className="section-heading">
            <span className="eyebrow">Профил</span>
            <h2>{application.applicant.fullName}</h2>
          </div>
          <dl className="definition-list">
            <div>
              <dt>Искана сума</dt>
              <dd>{formatCurrency(application.financial.requestedAmount)}</dd>
            </div>
            <div>
              <dt>Доказан доход</dt>
              <dd>{formatCurrency(score.totalIncome)}</dd>
            </div>
            <div>
              <dt>Цел</dt>
              <dd>{purposeLabels[application.financial.purpose]}</dd>
            </div>
          </dl>
        </div>
      </section>

      <section className="panel">
        <div className="panel-header">
          <div>
            <span className="eyebrow">Препоръчани маршрути</span>
            <h2>Най-подходящи банки към момента</h2>
          </div>
          <span className="pill muted">Ориентировъчни данни</span>
        </div>
        <div className="match-grid">
          {topMatches.map((match) => (
            <BankMatchItem key={match.product.id} match={match} compact />
          ))}
        </div>
      </section>
    </div>
  );
}

function ClientApplicationForm({
  application,
  score,
  updateApplicant,
  updateFinancial,
  saveStatus,
  saveMessage,
  onSave
}: {
  application: Application;
  score: ReturnType<typeof scoreApplication>;
  updateApplicant: (field: keyof Application["applicant"], value: string | number | boolean) => void;
  updateFinancial: (field: keyof Application["financial"], value: string | number) => void;
  saveStatus: SaveStatus;
  saveMessage: string;
  onSave: () => void;
}) {
  return (
    <div className="two-column-layout">
      <section className="panel">
        <div className="panel-header">
          <div>
            <span className="eyebrow">Клиентски данни</span>
            <h2>Личен и доходен профил</h2>
          </div>
          <SaveControl status={saveStatus} message={saveMessage} onSave={onSave} />
        </div>
        <div className="form-grid">
          <TextField label="Имена" value={application.applicant.fullName} onChange={(value) => updateApplicant("fullName", value)} />
          <TextField label="Имейл" value={application.applicant.email} onChange={(value) => updateApplicant("email", value)} />
          <TextField label="Телефон" value={application.applicant.phone} onChange={(value) => updateApplicant("phone", value)} />
          <TextField label="Град" value={application.applicant.city} onChange={(value) => updateApplicant("city", value)} />
          <NumberField label="Възраст" value={application.applicant.age} onChange={(value) => updateApplicant("age", value)} />
          <NumberField label="Месеци заетост" value={application.applicant.employmentMonths} onChange={(value) => updateApplicant("employmentMonths", value)} />
          <SelectField
            label="Тип заетост"
            value={application.applicant.employmentType}
            options={employmentLabels}
            onChange={(value) => updateApplicant("employmentType", value)}
          />
          <NumberField label="Членове домакинство" value={application.applicant.householdMembers} onChange={(value) => updateApplicant("householdMembers", value)} />
          <NumberField label="Деца под 18 г." value={application.applicant.childrenUnder18} onChange={(value) => updateApplicant("childrenUnder18", value)} />
          <CheckboxField
            label="Възможен превод на заплата"
            checked={application.applicant.salaryTransferPossible}
            onChange={(value) => updateApplicant("salaryTransferPossible", value)}
          />
        </div>
      </section>

      <section className="panel">
        <div className="section-heading">
          <span className="eyebrow">Кредитни параметри</span>
          <h2>Сума, имот и задължения</h2>
        </div>
        <div className="form-grid">
          <SelectField
            label="Цел"
            value={application.financial.purpose}
            options={purposeLabels}
            onChange={(value) => updateFinancial("purpose", value)}
          />
          <SelectField
            label="Етап на имот"
            value={application.financial.constructionStage}
            options={stageLabels}
            onChange={(value) => updateFinancial("constructionStage", value)}
          />
          <NumberField label="Нетен доход" value={application.financial.netMonthlyIncome} onChange={(value) => updateFinancial("netMonthlyIncome", value)} />
          <NumberField label="Доход съдлъжник" value={application.financial.coApplicantIncome} onChange={(value) => updateFinancial("coApplicantIncome", value)} />
          <NumberField label="Друг доход" value={application.financial.otherMonthlyIncome} onChange={(value) => updateFinancial("otherMonthlyIncome", value)} />
          <NumberField label="Текущи месечни вноски" value={application.financial.existingMonthlyDebt} onChange={(value) => updateFinancial("existingMonthlyDebt", value)} />
          <NumberField label="Искана сума" value={application.financial.requestedAmount} onChange={(value) => updateFinancial("requestedAmount", value)} />
          <NumberField label="Стойност на имот" value={application.financial.propertyValue} onChange={(value) => updateFinancial("propertyValue", value)} />
          <NumberField label="Самоучастие" value={application.financial.ownContribution} onChange={(value) => updateFinancial("ownContribution", value)} />
          <NumberField label="Декларирани спестявания" value={application.financial.declaredSavings} onChange={(value) => updateFinancial("declaredSavings", value)} />
          <NumberField label="Срок години" value={application.financial.termYears} onChange={(value) => updateFinancial("termYears", value)} />
        </div>
      </section>

      <section className="panel sticky-panel">
        <ScorePanel score={score} />
      </section>
    </div>
  );
}

function ResultsView({ application, score }: { application: Application; score: ReturnType<typeof scoreApplication> }) {
  return (
    <div className="content-stack">
      <section className="summary-grid">
        <ScorePanel score={score} />
        <KpiPanel icon={<PieChart />} label="DTI след кредита" value={formatPercent(score.dti)} helper="Дълг/доход с новата вноска" />
        <KpiPanel icon={<Building2 />} label="LTV" value={application.financial.purpose === "consumer" ? "n/a" : formatPercent(score.ltv)} helper="Кредит/стойност на имота" />
      </section>

      <section className="panel">
        <div className="panel-header">
          <div>
            <span className="eyebrow">Проследимост на правилата</span>
            <h2>Как системата стигна до оценката</h2>
          </div>
          <span className={classNames("route-badge", score.autoRoute)}>{routeLabel[score.autoRoute]}</span>
        </div>
        <div className="trace-list">
          {score.findings.map((finding) => (
            <TraceItem key={finding.label} item={finding} />
          ))}
        </div>
      </section>

      <section className="panel">
        <div className="panel-header">
          <div>
            <span className="eyebrow">Банково съвпадение</span>
            <h2>Сравнение на продукти</h2>
          </div>
          <span className="pill muted">Сортирано по оценка за съвпадение</span>
        </div>
        <div className="match-list">
          {score.bankMatches.slice(0, 7).map((match) => (
            <BankMatchItem key={match.product.id} match={match} />
          ))}
        </div>
      </section>
    </div>
  );
}

function DocumentsView({
  application,
  score,
  toggleConsent,
  updateDocumentStatus,
  downloadCasePack,
  saveStatus,
  saveMessage,
  onSave
}: {
  application: Application;
  score: ReturnType<typeof scoreApplication>;
  toggleConsent: (field: keyof Application["consents"]) => void;
  updateDocumentStatus: (documentId: string, status: DocumentItem["status"]) => void;
  downloadCasePack: (application: Application) => void;
  saveStatus: SaveStatus;
  saveMessage: string;
  onSave: () => void;
}) {
  const consentItems = [
    ["precontractInfo", "Преддоговорна информация"],
    ["personalDataNotice", "Уведомление за лични данни"],
    ["mediationAgreement", "Споразумение за кредитно посредничество"],
    ["bankSharingConsent", "Съгласие за споделяне към банки"],
    ["marketingConsent", "Маркетингово съгласие"]
  ] as const;

  return (
    <div className="two-column-layout documents-layout">
      <section className="panel">
        <div className="panel-header">
          <div>
            <span className="eyebrow">Съгласия</span>
            <h2>Версия {application.consents.documentVersion}</h2>
          </div>
          <SaveControl status={saveStatus} message={saveMessage} onSave={onSave} />
        </div>
        <div className="check-list">
          {consentItems.map(([field, label]) => (
            <label key={field} className="check-row">
              <input
                type="checkbox"
                checked={Boolean(application.consents[field])}
                onChange={() => toggleConsent(field)}
              />
              <span>{label}</span>
            </label>
          ))}
        </div>
        <p className="muted-text">
          Последно приемане: {new Date(application.consents.acceptedAt).toLocaleString("bg-BG")}. В продукционна среда тук се пазят
          версия, IP, потребител, правно основание и запис в журнала.
        </p>
      </section>

      <section className="panel">
        <div className="section-heading">
          <span className="eyebrow">Документи</span>
          <h2>Комплект за банка</h2>
        </div>
        <div className="document-list">
          {application.documents.map((document) => (
            <div className="document-row" key={document.id}>
              <div>
                <strong>{document.label}</strong>
                <span>{document.owner === "client" ? "Клиент" : document.owner === "consultant" ? "Консултант" : "Банка"}</span>
              </div>
              <select value={document.status} onChange={(event) => updateDocumentStatus(document.id, event.target.value as DocumentItem["status"])}>
                <option value="received">Получен</option>
                <option value="missing">Липсва</option>
                <option value="needs_update">За обновяване</option>
              </select>
            </div>
          ))}
        </div>
      </section>

      <section className="panel sticky-panel">
        <div className="section-heading">
          <span className="eyebrow">Изход</span>
          <h2>Пакет за банката</h2>
        </div>
        <p>Експортът съдържа заявление, оценка, най-подходящи банкови съвпадения и причините за насочване. В продукционна среда това става PDF/ZIP с електронен подпис.</p>
        <button className="primary-button" type="button" onClick={() => downloadCasePack(application)}>
          <ArrowDownToLine size={17} />
          Свали демонстрационен JSON пакет
        </button>
        <div className="trace-list compact">
          {score.missingDocuments.length === 0 ? (
            <TraceItem item={{ label: "Няма липсващи минимални документи.", tone: "positive" }} />
          ) : (
            score.missingDocuments.map((document) => (
              <TraceItem key={document.id} item={{ label: `Липсва: ${document.label}`, tone: "warning" }} />
            ))
          )}
        </div>
      </section>
    </div>
  );
}

function ClientBankFormsView({
  application,
  tasks,
  requests,
  templates,
  updateBankFormValue,
  submitBankFormRequest
}: {
  application: Application;
  tasks: ClientTask[];
  requests: BankFormRequest[];
  templates: BankFormTemplate[];
  updateBankFormValue: (requestId: string, fieldId: string, value: string | number | boolean) => void;
  submitBankFormRequest: (requestId: string) => void;
}) {
  return (
    <div className="content-stack">
      <section className="summary-grid">
        <KpiPanel icon={<FileText />} label="Възложени форми" value={String(requests.length)} helper="От кредитен консултант" />
        <KpiPanel icon={<Bell />} label="Отворени задачи" value={String(tasks.filter((task) => task.status !== "done").length)} helper="Документи, съгласия и форми" />
        <KpiPanel icon={<ShieldCheck />} label="Съгласия" value={application.consents.bankSharingConsent ? "Активни" : "Липсват"} helper="Изискват се преди изпращане към банка" />
      </section>

      <section className="panel">
        <div className="panel-header">
          <div>
            <span className="eyebrow">Задачи към клиента</span>
            <h2>Какво трябва да се попълни преди банков пакет</h2>
          </div>
          <span className="pill muted">Всичко се записва в журнал</span>
        </div>
        <div className="task-grid">
          {tasks.map((task) => (
            <article className="task-item" key={task.id}>
              <div>
                <span className={classNames("status-pill", task.status)}>{taskStatusLabels[task.status]}</span>
                <strong>{task.title}</strong>
                <p>{task.description}</p>
              </div>
              <small>Срок: {new Date(task.dueDate).toLocaleDateString("bg-BG")}</small>
            </article>
          ))}
          {tasks.length === 0 && <p className="muted-text">Няма активни задачи към клиента.</p>}
        </div>
      </section>

      {requests.map((request) => {
        const template = templates.find((item) => item.id === request.templateId);
        if (!template) return null;
        return (
          <section className="panel" key={request.id}>
            <div className="panel-header">
              <div>
                <span className="eyebrow">{template.bankName} · версия {template.version}</span>
                <h2>{template.title}</h2>
              </div>
              <span className={classNames("status-pill", request.status)}>{formRequestStatusLabels[request.status]}</span>
            </div>
            <p>{template.description}</p>
            <div className="notice-box">
              <ShieldCheck size={18} />
              <span>
                Данните се използват само за конкретната банкова форма. В продукционна среда ЕГН, лични документи и файлове трябва
                да се криптират и да са достъпни само за оправомощени роли.
              </span>
            </div>
            <BankFormRenderer
              request={request}
              template={template}
              readonly={request.status === "submitted" || request.status === "verified"}
              updateBankFormValue={updateBankFormValue}
            />
            <div className="button-row">
              <button
                className="primary-button"
                type="button"
                disabled={request.status === "submitted" || request.status === "verified"}
                onClick={() => submitBankFormRequest(request.id)}
              >
                <Send size={17} />
                Подай формата към консултант
              </button>
            </div>
          </section>
        );
      })}
    </div>
  );
}

function BankFormRenderer({
  request,
  template,
  readonly,
  updateBankFormValue
}: {
  request: BankFormRequest;
  template: BankFormTemplate;
  readonly?: boolean;
  updateBankFormValue: (requestId: string, fieldId: string, value: string | number | boolean) => void;
}) {
  const sections = Array.from(new Set(template.fields.filter((field) => !field.consultantOnly).map((field) => field.section)));

  return (
    <div className="bank-form-renderer">
      {sections.map((section) => (
        <div className="form-section" key={section}>
          <h3>{section}</h3>
          <div className="form-grid">
            {template.fields
              .filter((field) => field.section === section && !field.consultantOnly)
              .map((field) => {
                const value = request.values[field.id] ?? "";
                if (field.type === "select") {
                  return (
                    <label className="field" key={field.id}>
                      <span>{field.label}{field.required ? " *" : ""}</span>
                      <select
                        value={String(value)}
                        disabled={readonly}
                        onChange={(event) => updateBankFormValue(request.id, field.id, event.target.value)}
                      >
                        <option value="">Изберете</option>
                        {(field.options ?? []).map((option) => (
                          <option key={option} value={option}>{option}</option>
                        ))}
                      </select>
                      {field.helpText && <small>{field.helpText}</small>}
                    </label>
                  );
                }

                if (field.type === "yes_no") {
                  return (
                    <label className="field" key={field.id}>
                      <span>{field.label}{field.required ? " *" : ""}</span>
                      <select
                        value={value === true ? "yes" : value === false ? "no" : ""}
                        disabled={readonly}
                        onChange={(event) => updateBankFormValue(request.id, field.id, event.target.value === "yes")}
                      >
                        <option value="">Изберете</option>
                        <option value="yes">Да</option>
                        <option value="no">Не</option>
                      </select>
                    </label>
                  );
                }

                if (field.type === "checkbox") {
                  return (
                    <label className="checkbox-field" key={field.id}>
                      <input
                        type="checkbox"
                        checked={Boolean(value)}
                        disabled={readonly}
                        onChange={(event) => updateBankFormValue(request.id, field.id, event.target.checked)}
                      />
                      <span>{field.label}{field.required ? " *" : ""}</span>
                    </label>
                  );
                }

                if (field.type === "textarea") {
                  return (
                    <label className="field wide-field" key={field.id}>
                      <span>{field.label}{field.required ? " *" : ""}</span>
                      <textarea
                        value={String(value)}
                        disabled={readonly}
                        onChange={(event) => updateBankFormValue(request.id, field.id, event.target.value)}
                      />
                    </label>
                  );
                }

                if (field.type === "file") {
                  return (
                    <div className="field" key={field.id}>
                      <span>{field.label}{field.required ? " *" : ""}</span>
                      <div className="file-placeholder">Качване на файл - продукционно хранилище към R2/S3</div>
                    </div>
                  );
                }

                return (
                  <label className="field" key={field.id}>
                    <span>{field.label}{field.required ? " *" : ""}</span>
                    <input
                      type={field.type === "number" ? "number" : field.type === "date" ? "date" : "text"}
                      value={String(value)}
                      disabled={readonly}
                      onChange={(event) =>
                        updateBankFormValue(
                          request.id,
                          field.id,
                          field.type === "number" ? Number(event.target.value) : event.target.value
                        )
                      }
                    />
                    {field.helpText && <small>{field.helpText}</small>}
                  </label>
                );
              })}
          </div>
        </div>
      ))}
    </div>
  );
}

function ConsultantPipeline({
  applications,
  products,
  selectedApplicationId,
  searchTerm,
  setSearchTerm,
  onSelect
}: {
  applications: Application[];
  products: BankProduct[];
  selectedApplicationId: string;
  searchTerm: string;
  setSearchTerm: (value: string) => void;
  onSelect: (id: string) => void;
}) {
  const scored = applications.map((application) => ({ application, score: scoreApplication(application, products) }));
  const filtered = scored.filter(({ application }) =>
    `${application.applicant.fullName} ${application.id} ${application.applicant.city}`.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="content-stack">
      <section className="summary-grid">
        <KpiPanel icon={<ClipboardCheck />} label="Активни заявления" value={String(applications.length)} helper="В консултантския списък" />
        <KpiPanel
          icon={<Bell />}
          label="За действие"
          value={String(scored.filter((item) => item.score.autoRoute !== "auto_to_bank").length)}
          helper="Преглед или документи"
        />
        <KpiPanel
          icon={<Banknote />}
          label="Готови съвпадения"
          value={String(scored.reduce((sum, item) => sum + item.score.bankMatches.filter((match) => match.status === "match").length, 0))}
          helper="Потенциални банкови маршрути"
        />
      </section>

      <section className="panel">
        <div className="panel-header">
          <div>
            <span className="eyebrow">Списък със заявления</span>
            <h2>Входящи клиентски досиета</h2>
          </div>
          <label className="search-box">
            <Search size={16} />
            <input value={searchTerm} placeholder="Търси клиент, град, ID" onChange={(event) => setSearchTerm(event.target.value)} />
          </label>
        </div>
        <div className="application-table">
          {filtered.map(({ application, score }) => (
            <button
              className={classNames("application-row", application.id === selectedApplicationId && "selected")}
              key={application.id}
              type="button"
              onClick={() => onSelect(application.id)}
            >
              <div>
                <strong>{application.applicant.fullName}</strong>
                <span>{application.id} · {application.applicant.city}</span>
              </div>
              <span>{purposeLabels[application.financial.purpose]}</span>
              <span>{formatCurrency(application.financial.requestedAmount)}</span>
              <span className={classNames("score-chip", score.risk)}>{score.score}</span>
              <span className={classNames("route-badge", score.autoRoute)}>{routeLabel[score.autoRoute]}</span>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}

function ConsultantCase({
  application,
  score,
  formRequests,
  formTemplates,
  onStatusChange,
  onRequestBankForm,
  onVerifyBankForm,
  downloadCasePack
}: {
  application: Application;
  score: ReturnType<typeof scoreApplication>;
  formRequests: BankFormRequest[];
  formTemplates: BankFormTemplate[];
  onStatusChange: (applicationId: string, status: ApplicationStatus) => void;
  onRequestBankForm: (applicationId: string, templateId: string, consultantNote: string) => void;
  onVerifyBankForm: (requestId: string) => void;
  downloadCasePack: (application: Application) => void;
}) {
  const activeTemplates = formTemplates.filter((template) => template.isActive);

  return (
    <div className="case-grid">
      <section className="panel case-main">
        <div className="panel-header">
          <div>
            <span className="eyebrow">{application.id}</span>
            <h2>{application.applicant.fullName}</h2>
          </div>
          <span className={classNames("status-pill", application.status)}>{statusLabels[application.status]}</span>
        </div>

        <div className="summary-grid inner">
          <KpiPanel icon={<Gauge />} label="Оценка" value={String(score.score)} helper={riskLabel[score.risk]} />
          <KpiPanel icon={<PieChart />} label="DTI" value={formatPercent(score.dti)} helper="След новата вноска" />
          <KpiPanel icon={<Building2 />} label="LTV" value={application.financial.purpose === "consumer" ? "n/a" : formatPercent(score.ltv)} helper="Искана сума / имот" />
        </div>

        <div className="split-section">
          <div>
            <h3>Клиентски профил</h3>
            <dl className="definition-list">
              <div><dt>Град</dt><dd>{application.applicant.city}</dd></div>
              <div><dt>Доход</dt><dd>{formatCurrency(score.totalIncome)}</dd></div>
              <div><dt>Заетост</dt><dd>{employmentLabels[application.applicant.employmentType]}, {application.applicant.employmentMonths} м.</dd></div>
              <div><dt>Цел</dt><dd>{purposeLabels[application.financial.purpose]}</dd></div>
              <div><dt>Имот</dt><dd>{stageLabels[application.financial.constructionStage]}</dd></div>
            </dl>
          </div>
          <div>
            <h3>Действия</h3>
            <div className="button-row">
              <button className="secondary-button" type="button" onClick={() => onStatusChange(application.id, "missing_documents")}>Изискай документи</button>
              <button className="secondary-button" type="button" onClick={() => onStatusChange(application.id, "bank_routes_selected")}>Маркирай избрани банки</button>
              <button className="secondary-button" type="button" onClick={() => onStatusChange(application.id, "bank_ready")}>Маркирай готова</button>
              <button className="primary-button" type="button" onClick={() => downloadCasePack(application)}>
                <ArrowDownToLine size={17} />
                Експортирай
              </button>
            </div>
          </div>
        </div>

        <ConsultantBankFormPanel
          application={application}
          formRequests={formRequests}
          activeTemplates={activeTemplates}
          onRequestBankForm={onRequestBankForm}
          onVerifyBankForm={onVerifyBankForm}
        />

        <div className="section-heading">
          <span className="eyebrow">Банково насочване</span>
          <h2>Подходящи и отпадащи продукти</h2>
        </div>
        <div className="match-list">
          {score.bankMatches.slice(0, 8).map((match) => (
            <BankMatchItem key={match.product.id} match={match} />
          ))}
        </div>
      </section>

      <aside className="panel case-side">
        <ScorePanel score={score} />
        <div className="section-heading">
          <span className="eyebrow">Липси</span>
          <h2>Документи</h2>
        </div>
        <div className="trace-list compact">
          {score.missingDocuments.length === 0 ? (
            <TraceItem item={{ label: "Няма критични липси.", tone: "positive" }} />
          ) : (
            score.missingDocuments.map((document) => (
              <TraceItem key={document.id} item={{ label: document.label, tone: "warning" }} />
            ))
          )}
        </div>
        <div className="section-heading">
          <span className="eyebrow">Бележки</span>
          <h2>Консултантски контекст</h2>
        </div>
        <ul className="note-list">
          {application.notes.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      </aside>
    </div>
  );
}

function ConsultantBankFormPanel({
  application,
  formRequests,
  activeTemplates,
  onRequestBankForm,
  onVerifyBankForm
}: {
  application: Application;
  formRequests: BankFormRequest[];
  activeTemplates: BankFormTemplate[];
  onRequestBankForm: (applicationId: string, templateId: string, consultantNote: string) => void;
  onVerifyBankForm: (requestId: string) => void;
}) {
  const [selectedTemplateId, setSelectedTemplateId] = useState(activeTemplates[0]?.id ?? "");
  const [note, setNote] = useState("Моля, попълнете банковата форма и потвърдете предварително попълнените данни.");

  return (
    <section className="panel nested-panel">
      <div className="panel-header">
        <div>
          <span className="eyebrow">Банкови форми</span>
          <h2>Възлагане към клиент</h2>
        </div>
        <span className="pill muted">{formRequests.length} активни/исторически заявки</span>
      </div>

      <div className="request-form-grid">
        <label className="field">
          <span>Избери банкова форма</span>
          <select value={selectedTemplateId} onChange={(event) => setSelectedTemplateId(event.target.value)}>
            {activeTemplates.map((template) => (
              <option key={template.id} value={template.id}>
                {template.bankName} - {template.title} v{template.version}
              </option>
            ))}
          </select>
        </label>
        <label className="field wide-field">
          <span>Бележка към клиента</span>
          <textarea value={note} onChange={(event) => setNote(event.target.value)} />
        </label>
        <button
          className="primary-button"
          type="button"
          disabled={!selectedTemplateId}
          onClick={() => onRequestBankForm(application.id, selectedTemplateId, note)}
        >
          <Send size={17} />
          Изискай форма
        </button>
      </div>

      <div className="request-list">
        {formRequests.map((request) => {
          const template = activeTemplates.find((item) => item.id === request.templateId);
          return (
            <article className="request-item" key={request.id}>
              <div>
                <span className={classNames("status-pill", request.status)}>{formRequestStatusLabels[request.status]}</span>
                <strong>{template?.title ?? request.templateId}</strong>
                <p>{request.consultantNote}</p>
                <small>Срок: {new Date(request.dueDate).toLocaleDateString("bg-BG")}</small>
              </div>
              <div className="button-row">
                <button
                  className="secondary-button"
                  type="button"
                  disabled={request.status !== "submitted"}
                  onClick={() => onVerifyBankForm(request.id)}
                >
                  <CheckCircle2 size={16} />
                  Маркирай проверена
                </button>
              </div>
            </article>
          );
        })}
        {formRequests.length === 0 && <p className="muted-text">Все още няма изискани банкови форми за това досие.</p>}
      </div>
    </section>
  );
}

function MessagesView({ applications, products }: { applications: Application[]; products: BankProduct[] }) {
  return (
    <section className="panel">
      <div className="section-heading">
        <span className="eyebrow">Комуникация</span>
        <h2>Автоматични следващи съобщения</h2>
      </div>
      <div className="message-list">
        {applications.map((application) => {
          const score = scoreApplication(application, products);
          return (
            <div className="message-item" key={application.id}>
              <div className="message-avatar">{application.applicant.fullName.slice(0, 1)}</div>
              <div>
                <strong>{application.applicant.fullName}</strong>
                <p>
                  {score.autoRoute === "request_documents"
                    ? `Моля, качете: ${score.missingDocuments.map((item) => item.label).join(", ")}.`
                    : score.autoRoute === "auto_to_bank"
                      ? "Профилът е готов за изпращане към препоръчаните банки."
                      : "Заявката е получена и ще бъде прегледана от консултант."}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

function ProductsAdmin({
  products,
  saveProduct,
  archiveProduct
}: {
  products: BankProduct[];
  saveProduct: (product: BankProduct) => Promise<BankProduct | null>;
  archiveProduct: (productId: string) => Promise<BankProduct | null>;
}) {
  const [draft, setDraft] = useState<BankProduct>(emptyBankProduct);
  const [strengthsText, setStrengthsText] = useState(draft.strengths.join("\n"));
  const [cautionsText, setCautionsText] = useState(draft.cautions.join("\n"));
  const [status, setStatus] = useState("");
  const activeCount = products.filter((product) => product.isActive !== false).length;

  const startNew = () => {
    const next = emptyBankProduct();
    setDraft(next);
    setStrengthsText(next.strengths.join("\n"));
    setCautionsText(next.cautions.join("\n"));
    setStatus("");
  };

  const editProduct = (product: BankProduct) => {
    setDraft(structuredClone(product));
    setStrengthsText(product.strengths.join("\n"));
    setCautionsText(product.cautions.join("\n"));
    setStatus("");
  };

  const updateStage = (stage: ConstructionStage, checked: boolean) => {
    setDraft((product) => {
      const acceptedStages = checked
        ? Array.from(new Set([...product.acceptedStages, stage]))
        : product.acceptedStages.filter((item) => item !== stage);
      return { ...product, acceptedStages: acceptedStages.length > 0 ? acceptedStages : product.acceptedStages };
    });
  };

  const handleSave = async () => {
    const product = {
      ...draft,
      strengths: strengthsText.split("\n").map((item) => item.trim()).filter(Boolean),
      cautions: cautionsText.split("\n").map((item) => item.trim()).filter(Boolean),
      updatedAt: new Date().toISOString()
    };
    const saved = await saveProduct(product);
    if (saved) {
      editProduct(saved);
      setStatus("Продуктът е записан.");
    } else {
      setStatus("Продуктът не беше записан.");
    }
  };

  const handleArchive = async (productId: string) => {
    const archived = await archiveProduct(productId);
    setStatus(archived ? "Продуктът е архивиран." : "Продуктът не беше архивиран.");
    if (archived?.id === draft.id) editProduct(archived);
  };

  return (
    <div className="content-stack">
      <section className="summary-grid">
        <KpiPanel icon={<Landmark />} label="Банкови продукта" value={String(products.length)} helper={`${activeCount} активни в scoring`} />
        <KpiPanel icon={<BadgeCheck />} label="Онлайн процес" value={String(products.filter((product) => product.supportsOnlineFlow).length)} helper="Подходящи за дигитално насочване" />
        <KpiPanel icon={<Route />} label="Ипотечни маршрути" value={String(products.filter((product) => product.productType === "mortgage").length)} helper="Покупка, строеж, рефинансиране" />
      </section>

      <section className="panel">
        <div className="panel-header">
          <div>
            <span className="eyebrow">Редактор</span>
            <h2>Управление на банков продукт</h2>
          </div>
          <div className="button-row">
            <button className="secondary-button" type="button" onClick={startNew}>
              <Plus size={17} />
              Нов продукт
            </button>
            <button className="primary-button" type="button" onClick={handleSave}>
              <Save size={17} />
              Запази
            </button>
          </div>
        </div>

        <div className="admin-form-grid dense">
          <TextField label="Банка" value={draft.bankName} onChange={(value) => setDraft((product) => ({ ...product, bankName: value }))} />
          <TextField label="Продукт" value={draft.productName} onChange={(value) => setDraft((product) => ({ ...product, productName: value }))} />
          <SelectField label="Тип" value={draft.productType} options={productTypeLabels} onChange={(value) => setDraft((product) => ({ ...product, productType: value }))} />
          <CheckboxField label="Активен в автоматичното насочване" checked={draft.isActive !== false} onChange={(value) => setDraft((product) => ({ ...product, isActive: value }))} />
          <NumberField label="Мин. лихва %" value={draft.minRate} onChange={(value) => setDraft((product) => ({ ...product, minRate: value }))} />
          <NumberField label="Макс. лихва %" value={draft.maxRate} onChange={(value) => setDraft((product) => ({ ...product, maxRate: value }))} />
          <NumberField label="ГПР от %" value={draft.aprFrom ?? draft.maxRate} onChange={(value) => setDraft((product) => ({ ...product, aprFrom: value }))} />
          <NumberField label="Макс. сума" value={draft.maxAmount} onChange={(value) => setDraft((product) => ({ ...product, maxAmount: value }))} />
          <NumberField label="Макс. срок години" value={draft.maxTermYears} onChange={(value) => setDraft((product) => ({ ...product, maxTermYears: value }))} />
          <NumberField label="Макс. LTV" value={draft.maxLtv} onChange={(value) => setDraft((product) => ({ ...product, maxLtv: value }))} />
          <NumberField label="Макс. DTI" value={draft.maxDti} onChange={(value) => setDraft((product) => ({ ...product, maxDti: value }))} />
          <NumberField label="Мин. доход" value={draft.minIncome} onChange={(value) => setDraft((product) => ({ ...product, minIncome: value }))} />
          <NumberField label="Мин. месеци заетост" value={draft.minEmploymentMonths} onChange={(value) => setDraft((product) => ({ ...product, minEmploymentMonths: value }))} />
          <TextField label="Източник" value={draft.sourceLabel} onChange={(value) => setDraft((product) => ({ ...product, sourceLabel: value }))} />
          <TextField label="URL източник" value={draft.sourceUrl} onChange={(value) => setDraft((product) => ({ ...product, sourceUrl: value }))} />
          <TextField label="Последна проверка" value={draft.lastChecked} onChange={(value) => setDraft((product) => ({ ...product, lastChecked: value }))} />
          <label className="field wide-field">
            <span>Кратко описание</span>
            <textarea value={draft.headline} onChange={(event) => setDraft((product) => ({ ...product, headline: event.target.value }))} />
          </label>
          <div className="wide-field stage-picker">
            <span>Приети етапи на имот</span>
            <div>
              {(Object.keys(stageLabels) as ConstructionStage[]).map((stage) => (
                <CheckboxField
                  key={stage}
                  label={stageLabels[stage]}
                  checked={draft.acceptedStages.includes(stage)}
                  onChange={(checked) => updateStage(stage, checked)}
                />
              ))}
            </div>
          </div>
          <CheckboxField label="Изисква превод на заплата" checked={draft.requiresSalaryTransfer} onChange={(value) => setDraft((product) => ({ ...product, requiresSalaryTransfer: value }))} />
          <CheckboxField label="Поддържа онлайн процес" checked={draft.supportsOnlineFlow} onChange={(value) => setDraft((product) => ({ ...product, supportsOnlineFlow: value }))} />
          <label className="field wide-field">
            <span>Силни страни - по един ред</span>
            <textarea value={strengthsText} onChange={(event) => setStrengthsText(event.target.value)} />
          </label>
          <label className="field wide-field">
            <span>Условия за внимание - по един ред</span>
            <textarea value={cautionsText} onChange={(event) => setCautionsText(event.target.value)} />
          </label>
          {status && <span className="status-message wide-field">{status}</span>}
        </div>
      </section>

      <section className="panel">
        <div className="panel-header">
          <div>
            <span className="eyebrow">Каталог</span>
            <h2>Примерни банкови пакети</h2>
          </div>
          <span className="pill warning">Демонстрация, не оферта</span>
        </div>
        <div className="product-grid">
          {products.map((product) => (
            <article className="product-item" key={product.id}>
              <div className="product-header">
                <div>
                  <strong>{product.bankName}</strong>
                  <span>{product.productName}</span>
                </div>
                <span className={classNames("score-chip", product.isActive === false ? "critical" : "low")}>
                  {product.isActive === false ? "Архив" : `${product.minRate.toFixed(2)}%`}
                </span>
              </div>
              <p>{product.headline}</p>
              <dl className="compact-stats">
                <div><dt>Макс. сума</dt><dd>{formatCurrency(product.maxAmount)}</dd></div>
                <div><dt>LTV</dt><dd>{formatPercent(product.maxLtv)}</dd></div>
                <div><dt>DTI</dt><dd>{formatPercent(product.maxDti)}</dd></div>
              </dl>
              <div className="button-row">
                <button className="secondary-button" type="button" onClick={() => editProduct(product)}>
                  <PencilLine size={16} />
                  Редактирай
                </button>
                <button className="secondary-button" type="button" onClick={() => handleArchive(product.id)} disabled={product.isActive === false}>
                  <Archive size={16} />
                  Архивирай
                </button>
              </div>
              <a href={product.sourceUrl} target="_blank" rel="noreferrer">{product.sourceLabel}</a>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}

function BankFormsAdmin({
  templates,
  products,
  createBankFormTemplate,
  updateBankFormTemplate
}: {
  templates: BankFormTemplate[];
  products: BankProduct[];
  createBankFormTemplate: (input: NewBankFormTemplateInput) => Promise<void>;
  updateBankFormTemplate: (template: BankFormTemplate) => Promise<BankFormTemplate>;
}) {
  const activeTemplates = templates.filter((template) => template.isActive);
  const totalFields = templates.reduce((sum, template) => sum + template.fields.length, 0);
  const [bankName, setBankName] = useState("Нова банка");
  const [productId, setProductId] = useState(products[0]?.id ?? "");
  const [title, setTitle] = useState("Заявление за кредит");
  const [version, setVersion] = useState("2026.01");
  const [description, setDescription] = useState("Шаблон с базови полета за клиентски данни, кредитни параметри и декларация.");
  const [requiredConsentScope, setRequiredConsentScope] = useState<BankFormTemplate["requiredConsentScope"]>("specific_bank");
  const [documentIdsText, setDocumentIdsText] = useState("id-card\nincome-proof");
  const [draftFields, setDraftFields] = useState<BankFormField[]>([
    { id: "client_name", label: "Имена на клиент", type: "text", required: true, section: "Лични данни", mapsFrom: "applicant.fullName" },
    { id: "requested_amount", label: "Искан размер", type: "number", required: true, section: "Кредитни параметри", mapsFrom: "financial.requestedAmount" },
    { id: "bank_declaration", label: "Клиентът приема декларацията на банката", type: "checkbox", required: true, section: "Декларации" }
  ]);
  const [fieldDraft, setFieldDraft] = useState<BankFormField>(emptyBankFormField);
  const [fieldOptionsText, setFieldOptionsText] = useState("");
  const [status, setStatus] = useState("");

  const addDraftField = () => {
    const options = fieldOptionsText.split("\n").map((item) => item.trim()).filter(Boolean);
    const nextField = {
      ...fieldDraft,
      id: fieldDraft.id.trim().replace(/[^a-zA-Z0-9_-]/g, "_") || `field_${Date.now()}`,
      options: options.length > 0 ? options : undefined
    };
    setDraftFields((fields) => [...fields.filter((field) => field.id !== nextField.id), nextField]);
    setFieldDraft(emptyBankFormField());
    setFieldOptionsText("");
  };

  const removeDraftField = (fieldId: string) => {
    setDraftFields((fields) => fields.filter((field) => field.id !== fieldId));
  };

  const handleCreateTemplate = async () => {
    await createBankFormTemplate({
      bankName: bankName.trim() || "Нова банка",
      productId: productId || products[0]?.id || "custom-product",
      title: title.trim() || "Заявление за кредит",
      version: version.trim() || "2026.01",
      description: description.trim() || "Шаблон с базови полета.",
      requiredConsentScope,
      requiredDocumentIds: documentIdsText.split("\n").map((item) => item.trim()).filter(Boolean),
      fields: draftFields.length > 0 ? draftFields : [emptyBankFormField()]
    });
    setTitle("Заявление за кредит");
    setDescription("Шаблон с базови полета за клиентски данни, кредитни параметри и декларация.");
    setStatus("Шаблонът е създаден.");
  };

  return (
    <div className="content-stack">
      <section className="summary-grid">
        <KpiPanel icon={<FilePlus2 />} label="Шаблони на форми" value={String(templates.length)} helper="Версионирани по банка" />
        <KpiPanel icon={<BadgeCheck />} label="Активни версии" value={String(activeTemplates.length)} helper="Достъпни за възлагане" />
        <KpiPanel icon={<Database />} label="Полета" value={String(totalFields)} helper="Валидации и свързване с досие" />
      </section>

      <section className="panel">
        <div className="panel-header">
          <div>
            <span className="eyebrow">Администратор</span>
            <h2>Нова банкова форма</h2>
          </div>
          <button className="primary-button" type="button" onClick={handleCreateTemplate}>
            <FilePlus2 size={17} />
            Създай шаблон
          </button>
        </div>

        <div className="admin-form-grid">
          <TextField label="Банка" value={bankName} onChange={setBankName} />
          <label className="field">
            <span>Продукт</span>
            <select value={productId} onChange={(event) => setProductId(event.target.value)}>
              {products.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.bankName} - {product.productName}
                </option>
              ))}
            </select>
          </label>
          <TextField label="Заглавие" value={title} onChange={setTitle} />
          <TextField label="Версия" value={version} onChange={setVersion} />
          <label className="field">
            <span>Обхват на съгласие</span>
            <select value={requiredConsentScope} onChange={(event) => setRequiredConsentScope(event.target.value as BankFormTemplate["requiredConsentScope"])}>
              <option value="specific_bank">Конкретна банка</option>
              <option value="all_partner_banks">Всички партньорски банки</option>
            </select>
          </label>
          <label className="field wide-field">
            <span>Описание</span>
            <textarea value={description} onChange={(event) => setDescription(event.target.value)} />
          </label>
          <label className="field wide-field">
            <span>Задължителни document ids - по един ред</span>
            <textarea value={documentIdsText} onChange={(event) => setDocumentIdsText(event.target.value)} />
          </label>
        </div>

        <div className="nested-panel no-frame">
          <div className="panel-header">
            <div>
              <span className="eyebrow">Полета</span>
              <h2>Builder за банков шаблон</h2>
            </div>
            <button className="secondary-button" type="button" onClick={addDraftField}>
              <Plus size={17} />
              Добави поле
            </button>
          </div>

          <div className="admin-form-grid dense">
            <TextField label="ID на поле" value={fieldDraft.id} onChange={(value) => setFieldDraft((field) => ({ ...field, id: value }))} />
            <TextField label="Етикет" value={fieldDraft.label} onChange={(value) => setFieldDraft((field) => ({ ...field, label: value }))} />
            <label className="field">
              <span>Тип</span>
              <select value={fieldDraft.type} onChange={(event) => setFieldDraft((field) => ({ ...field, type: event.target.value as BankFormField["type"] }))}>
                {(Object.keys(fieldTypeLabels) as BankFormField["type"][]).map((type) => (
                  <option key={type} value={type}>{fieldTypeLabels[type]}</option>
                ))}
              </select>
            </label>
            <TextField label="Секция" value={fieldDraft.section} onChange={(value) => setFieldDraft((field) => ({ ...field, section: value }))} />
            <label className="field">
              <span>Свързване с досие</span>
              <select value={fieldDraft.mapsFrom ?? ""} onChange={(event) => setFieldDraft((field) => ({ ...field, mapsFrom: event.target.value || undefined }))}>
                <option value="">Без автоматично попълване</option>
                {Object.entries(mappedFieldLabels).map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </select>
            </label>
            <CheckboxField label="Задължително поле" checked={fieldDraft.required} onChange={(value) => setFieldDraft((field) => ({ ...field, required: value }))} />
            <CheckboxField label="Само за консултант" checked={Boolean(fieldDraft.consultantOnly)} onChange={(value) => setFieldDraft((field) => ({ ...field, consultantOnly: value }))} />
            <TextField label="Помощен текст" value={fieldDraft.helpText ?? ""} onChange={(value) => setFieldDraft((field) => ({ ...field, helpText: value }))} />
            <label className="field wide-field">
              <span>Опции за избор - по един ред</span>
              <textarea value={fieldOptionsText} onChange={(event) => setFieldOptionsText(event.target.value)} />
            </label>
          </div>

          <div className="field-preview-list editable">
            {draftFields.map((field) => (
              <div key={field.id}>
                <strong>{field.label}</strong>
                <span>{field.id} · {field.section} · {formFieldPreview(field)}</span>
                <button className="icon-button subtle" type="button" aria-label={`Премахни ${field.label}`} onClick={() => removeDraftField(field.id)}>
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
          {status && <span className="status-message">{status}</span>}
        </div>
      </section>

      <section className="panel">
        <div className="panel-header">
          <div>
            <span className="eyebrow">Каталог</span>
            <h2>Банкови форми по банка и продукт</h2>
          </div>
          <span className="pill muted">Версионирани шаблони</span>
        </div>

        <div className="form-template-grid">
          {templates.map((template) => {
            const product = products.find((item) => item.id === template.productId);
            const sections = Array.from(new Set(template.fields.map((field) => field.section)));
            return (
              <article className="form-template-card" key={template.id}>
                <div className="product-header">
                  <div>
                    <strong>{template.bankName}</strong>
                    <span>{template.title}</span>
                  </div>
                  <span className={classNames("status-pill", template.isActive ? "verified" : "archived")}>
                    {template.isActive ? "Активна" : "Архивирана"}
                  </span>
                </div>
                <p>{template.description}</p>
                <dl className="compact-stats">
                  <div><dt>Версия</dt><dd>{template.version}</dd></div>
                  <div><dt>Продукт</dt><dd>{product?.productName ?? template.productId}</dd></div>
                  <div><dt>Полета</dt><dd>{template.fields.length}</dd></div>
                  <div><dt>Документи</dt><dd>{template.requiredDocumentIds.length}</dd></div>
                </dl>
                <div className="section-chips">
                  {sections.map((section) => (
                    <span key={section}>{section}</span>
                  ))}
                </div>
                <div className="field-preview-list">
                  {template.fields.slice(0, 5).map((field) => (
                    <div key={field.id}>
                      <strong>{field.label}</strong>
                      <span>{formFieldPreview(field)}</span>
                    </div>
                  ))}
                </div>
                <div className="button-row">
                  <button
                    className="secondary-button"
                    type="button"
                    onClick={() => void updateBankFormTemplate({ ...template, isActive: !template.isActive })}
                  >
                    {template.isActive ? <Archive size={16} /> : <BadgeCheck size={16} />}
                    {template.isActive ? "Архивирай" : "Активирай"}
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function RulesAdmin({
  rules,
  saveRule
}: {
  rules: RuleDefinition[];
  saveRule: (rule: RuleDefinition) => Promise<RuleDefinition | null>;
}) {
  const [draft, setDraft] = useState<RuleDefinition>(emptyRuleDefinition);
  const [status, setStatus] = useState("");

  const startNew = () => {
    setDraft(emptyRuleDefinition());
    setStatus("");
  };

  const editRule = (rule: RuleDefinition) => {
    setDraft(structuredClone(rule));
    setStatus("");
  };

  const handleSave = async () => {
    const saved = await saveRule({ ...draft, updatedAt: new Date().toISOString() });
    if (saved) {
      editRule(saved);
      setStatus("Правилото е записано.");
    } else {
      setStatus("Правилото не беше записано.");
    }
  };

  const toggleRule = async (rule: RuleDefinition) => {
    const saved = await saveRule({ ...rule, active: !rule.active, updatedAt: new Date().toISOString() });
    setStatus(saved ? "Статусът на правилото е обновен." : "Статусът не беше обновен.");
  };

  return (
    <div className="content-stack">
      <section className="summary-grid">
        <KpiPanel icon={<SlidersHorizontal />} label="Правила" value={String(rules.length)} helper={`${rules.filter((rule) => rule.active).length} активни`} />
        <KpiPanel icon={<AlertTriangle />} label="Блокиращи" value={String(rules.filter((rule) => rule.severity === "blocker").length)} helper="Спират или ескалират случай" />
        <KpiPanel icon={<Workflow />} label="Категории" value="4" helper="Допустимост, риск, документи, насочване" />
      </section>

      <section className="panel">
        <div className="panel-header">
          <div>
            <span className="eyebrow">Редактор</span>
            <h2>Вътрешно правило</h2>
          </div>
          <div className="button-row">
            <button className="secondary-button" type="button" onClick={startNew}>
              <Plus size={17} />
              Ново правило
            </button>
            <button className="primary-button" type="button" onClick={handleSave}>
              <Save size={17} />
              Запази
            </button>
          </div>
        </div>
        <div className="admin-form-grid">
          <TextField label="Име" value={draft.name} onChange={(value) => setDraft((rule) => ({ ...rule, name: value }))} />
          <TextField label="Версия" value={draft.version ?? ""} onChange={(value) => setDraft((rule) => ({ ...rule, version: value }))} />
          <SelectField label="Категория" value={draft.category} options={ruleCategoryLabels} onChange={(value) => setDraft((rule) => ({ ...rule, category: value }))} />
          <SelectField label="Тежест" value={draft.severity} options={severityLabels} onChange={(value) => setDraft((rule) => ({ ...rule, severity: value }))} />
          <CheckboxField label="Активно правило" checked={draft.active} onChange={(value) => setDraft((rule) => ({ ...rule, active: value }))} />
          <label className="field wide-field">
            <span>Описание</span>
            <textarea value={draft.description} onChange={(event) => setDraft((rule) => ({ ...rule, description: event.target.value }))} />
          </label>
          <label className="field wide-field">
            <span>Условие</span>
            <textarea value={draft.condition ?? ""} onChange={(event) => setDraft((rule) => ({ ...rule, condition: event.target.value }))} />
          </label>
          <label className="field wide-field">
            <span>Ефект</span>
            <textarea value={draft.effect ?? ""} onChange={(event) => setDraft((rule) => ({ ...rule, effect: event.target.value }))} />
          </label>
          {status && <span className="status-message wide-field">{status}</span>}
        </div>
      </section>

      <section className="panel">
        <div className="panel-header">
          <div>
            <span className="eyebrow">Двигател за правила</span>
            <h2>Вътрешни правила</h2>
          </div>
          <span className="pill muted">Версиониран registry</span>
        </div>
        <div className="rules-list">
          {rules.map((rule) => (
            <div className="rule-row editable" key={rule.id}>
              <div>
                <strong>{rule.name}</strong>
                <p>{rule.description}</p>
                <span>{ruleCategoryLabels[rule.category]} · версия {rule.version ?? "n/a"}</span>
              </div>
              <span className={classNames("severity", rule.severity)}>{severityLabels[rule.severity]}</span>
              <label className="switch">
                <input type="checkbox" checked={rule.active} onChange={() => void toggleRule(rule)} />
                <span />
              </label>
              <button className="secondary-button" type="button" onClick={() => editRule(rule)}>
                <PencilLine size={16} />
                Редактирай
              </button>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

function AuditView({ events }: { events: AuditEvent[] }) {
  return (
    <section className="panel">
      <div className="section-heading">
        <span className="eyebrow">Журнал за действия</span>
        <h2>Проследимост на решенията</h2>
      </div>
      <div className="timeline">
        {events.map((event) => (
          <div className="timeline-item" key={event.id}>
            <span>{new Date(event.at).toLocaleString("bg-BG")}</span>
            <strong>{event.action}</strong>
            <p>{event.actor} · {event.target} · {event.details}</p>
          </div>
        ))}
      </div>
    </section>
  );
}

function ProductionView() {
  return (
    <div className="content-stack">
      <section className="panel">
        <div className="section-heading">
          <span className="eyebrow">Път към продукционна среда</span>
          <h2>Как демонстрационната версия става реална система</h2>
        </div>
        <div className="checklist-grid">
          {productionChecklist.map((item) => (
            <div className="checklist-item" key={item.label}>
              <span className={classNames("status-dot", item.status)} />
              <div>
                <strong>{item.label}</strong>
                <p>{item.detail}</p>
              </div>
            </div>
          ))}
        </div>
      </section>
      <section className="panel">
        <div className="section-heading">
          <span className="eyebrow">Архитектура</span>
          <h2>Препоръчана продукционна конфигурация</h2>
        </div>
        <div className="architecture-grid">
          <KpiPanel icon={<Building2 />} label="Хостинг" value="Cloudflare Pages" helper="Публичен портал и административен панел" />
          <KpiPanel icon={<Workflow />} label="Функции" value="Pages Functions" helper="Правила, журнал и експорти" />
          <KpiPanel icon={<Banknote />} label="Данни" value="D1 / Postgres" helper="Заявления, правила, версии, логове" />
          <KpiPanel icon={<ShieldCheck />} label="Сигурност" value="MFA + RBAC" helper="Роли, журнал и минимален достъп" />
        </div>
      </section>
    </div>
  );
}

function ScorePanel({ score }: { score: ReturnType<typeof scoreApplication> }) {
  return (
    <div className="score-panel">
      <div className="score-ring" style={{ "--score": `${score.score}%` } as CSSProperties}>
        <strong>{score.score}</strong>
        <span>оценка</span>
      </div>
      <div className="score-copy">
        <span className={classNames("risk-label", score.risk)}>{riskLabel[score.risk]} риск</span>
        <h2>{routeLabel[score.autoRoute]}</h2>
        <p>
          Очаквана вноска {formatCurrency(score.estimatedPayment)} · свободен остатък{" "}
          {formatCurrency(score.disposableAfterDebt)}
        </p>
      </div>
    </div>
  );
}

function KpiPanel({ icon, label, value, helper }: { icon: ReactNode; label: string; value: string; helper: string }) {
  return (
    <div className="kpi-panel">
      <div className="kpi-icon">{icon}</div>
      <div>
        <span>{label}</span>
        <strong>{value}</strong>
        <p>{helper}</p>
      </div>
    </div>
  );
}

function BankMatchItem({ match, compact = false }: { match: BankMatch; compact?: boolean }) {
  return (
    <article className={classNames("bank-match", match.status, compact && "compact")}>
      <div className="bank-match-top">
        <div>
          <strong>{match.product.bankName}</strong>
          <span>{match.product.productName}</span>
        </div>
        <span className={classNames("score-chip", match.status === "match" ? "low" : match.status === "review" ? "medium" : "critical")}>
          {match.fitScore}
        </span>
      </div>
      <p>{match.product.headline}</p>
      <dl className="compact-stats">
        <div><dt>Лихва от</dt><dd>{match.product.minRate.toFixed(2)}%</dd></div>
        <div><dt>Вноска</dt><dd>{formatCurrency(match.estimatedPayment)}</dd></div>
        <div><dt>Статус</dt><dd>{match.status === "match" ? "Подходящ" : match.status === "review" ? "Преглед" : "Отпада"}</dd></div>
      </dl>
      {!compact && (
        <div className="trace-list compact">
          {match.reasons.slice(0, 4).map((item) => (
            <TraceItem key={item.label} item={item} />
          ))}
        </div>
      )}
    </article>
  );
}

function TraceItem({ item }: { item: MatchReason }) {
  return (
    <div className={classNames("trace-item", item.tone)}>
      {toneIcon(item.tone)}
      <span>{item.label}</span>
    </div>
  );
}

function TextField({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
  return (
    <label className="field">
      <span>{label}</span>
      <input value={value} onChange={(event) => onChange(event.target.value)} />
    </label>
  );
}

function NumberField({ label, value, onChange }: { label: string; value: number; onChange: (value: number) => void }) {
  return (
    <label className="field">
      <span>{label}</span>
      <input type="number" value={value} onChange={(event) => onChange(Number(event.target.value))} />
    </label>
  );
}

function SelectField<T extends string>({
  label,
  value,
  options,
  onChange
}: {
  label: string;
  value: T;
  options: Record<T, string>;
  onChange: (value: T) => void;
}) {
  return (
    <label className="field">
      <span>{label}</span>
      <select value={value} onChange={(event) => onChange(event.target.value as T)}>
        {Object.entries(options).map(([key, labelValue]) => (
          <option key={key} value={key}>
            {String(labelValue)}
          </option>
        ))}
      </select>
    </label>
  );
}

function CheckboxField({ label, checked, onChange }: { label: string; checked: boolean; onChange: (value: boolean) => void }) {
  return (
    <label className="checkbox-field">
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
      <span>{label}</span>
    </label>
  );
}

export default App;
